import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Galleria sezione 6 — 8 slot, mapping 1:1 stretto (slot i ↔ GALLERY_PHOTOS[i]).
// Slot oltre la lunghezza dell'array mostrano placeholder "Foto in arrivo".
const GALLERY_SLOTS = 8;
const GALLERY_PHOTOS = [
  // 01 — Back hoodie Chiara De Bortoli n.10 con logo Reale Mutua
  "/images/assets/e4nbqsyw_01.jpg",
  // 02 — Chiara De Bortoli ritratto con palla, maglia Chieri '76 #10
  "/images/assets/m0tc53mo_02.jpg",
  // 03 — Jersey Chieri '76 #3 con sponsor Reale Mutua sul sedile del palazzetto
  "/images/assets/4i4oiijj_03.jpg",
  // 04 — Chiara De Bortoli ritratto, seduta, sguardo in camera
  "/images/assets/ne88alu5_04.jpg",
  // 05 — Poster mockup street ESSERE LIBERO con quote e key visual
  "/images/assets/d0h6o85m_05.jpg",
  // 06 — Sedili blu del PalaFenera, dettaglio tribuna
  "/images/assets/9a5bxli2_06.jpg",
  // 07 — Chiara De Bortoli si allaccia le scarpe a bordo campo (atmosfera teal)
  "/images/assets/23fjx3qp_07.jpg",
  // 08 — Chiara De Bortoli ritratto sorridente in sala pesi
  "/images/assets/dtthzr2d_08.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Essere Libero — Chiara De Bortoli, Chieri '76 — Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

const ReaeleMutuaEssereLibero = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const idx = WORKS.findIndex((w) => w.to === "/works/reale-mutua-essere-libero");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Reale Mutua Essere Libero", url: "/works/reale-mutua-essere-libero" },
  ]);

  return (
    <>
      <Seo
        title="Reale Mutua Essere Libero: format libero femminile | OnSports Case"
        description={"Format editoriale Reale Mutua \"Essere Libero\" sul ruolo del libero nel volley femminile con Chiara De Bortoli. OnSports Content & IP Studio, 2021."}
        path="/works/reale-mutua-essere-libero"
        image="/images/assets/e4nbqsyw_01.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/reale-mutua-essere-libero/hero.jpg"
        imageAlt="Essere Libero — Chiara De Bortoli, Chieri '76"
        eyebrow="● Case study · Cultural Impact · Content & IP Studio"
        titleLines={["Una sponsorship.", "Dentro un FORMAT."]}
        highlightWord="FORMAT."
        subtitle={"Abbiamo invitato Reale Mutua a entrare dentro un nostro format editoriale invece di commissionarne uno nuovo. Il risultato è \u201CEssere Libero\u201D: un video, un photo report e un articolo costruiti attorno alla pallavolista Chiara De Bortoli, libero del Reale Mutua Fenera Chieri '76. Amplificati su tre canali integrati per oltre 590mila impressions."}
        meta="2021  ·  Clienti — Reale Mutua · Chieri '76  ·  Categoria — Content & IP Studio"
        overlayOpacity={0.5}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Reale Mutua è sponsor di Reale Mutua Fenera Chieri '76, una delle
                    squadre più riconoscibili della Serie A1 femminile di pallavolo. Una
                    partnership commerciale con buona visibilità a bordo campo,
                    magliette e canali ufficiali — ma con il limite strutturale di tutte
                    le sponsorship che esauriscono il proprio valore nei 90 minuti della
                    partita. Quando il brand ci ha chiesto di trovare un modo diverso
                    di valorizzare la relazione, abbiamo proposto di non costruire un
                    nuovo format per Reale Mutua, ma di farla entrare dentro un format
                    che esisteva già: "Essere Libero", il nostro racconto editoriale
                    sulle figure dei libero — il ruolo più sottovalutato del volley.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Protagonista del progetto è stata Chiara De Bortoli, libero del
                    Chieri '76, classe 1997, già azzurra in nazionale maggiore. Abbiamo
                    costruito attorno a lei tre asset coordinati: un video editoriale
                    girato durante il match del 10 novembre 2021 al PalaFenera contro
                    la curva dei Fedelissimi, un photo report del backstage e della
                    partita, un articolo lungo pubblicato sui canali Problemi di Volley.
                    Il tutto distribuito su tre piattaforme integrate.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">Essere Libero</SchedaRow>
                    <SchedaRow label="Protagonista">
                      Chiara De Bortoli — Libero, Chieri '76
                    </SchedaRow>
                    <SchedaRow label="Clienti">
                      Reale Mutua · Reale Mutua Fenera Chieri '76
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Idea &amp; concept · Storyboard<br />
                      Produzione video e foto · Articolo editoriale<br />
                      Seeding e amplificazione cross-canale
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Format">
                      IP proprietaria Problemi di Volley
                    </SchedaRow>
                    <SchedaRow label="Anno">Novembre 2021</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Far esistere una sponsorship oltre la partita.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il problema operativo era classico: una sponsorship sportiva genera
                  valore concentrato sulle finestre del matchday, e poi diventa
                  silenziosa nei sei giorni della settimana che la separano dal match
                  successivo. Le settimane di pausa nazionali, le finestre di
                  calciomercato volley, gli infortuni — tutto contribuisce a indebolire
                  la curva di attenzione costruita la domenica.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La soluzione tradizionale è creare contenuti dedicati al brand,
                  prodotti dall'agenzia, pubblicati sui canali del club. È un modello
                  che funziona meccanicamente ma non genera affezione: il pubblico
                  riconosce un contenuto sponsorizzato e ne abbassa l'attenzione di
                  default. Abbiamo proposto a Reale Mutua una strada diversa: entrare
                  come supporter dentro un format editoriale già riconosciuto dalla
                  community, con un'autorialità che restava nostra e dei protagonisti
                  del volley.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL FORMAT / VIDEO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-format">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Essere libero</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Il ruolo più sottovalutato del volley, raccontato da chi lo gioca.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Il format "Essere Libero" è un racconto editoriale dedicato alle
                  figure del libero — il ruolo difensivo della pallavolo che riceve,
                  difende, copre, e che vive l'enorme paradosso di essere il più
                  decisivo e il meno visibile della squadra. Abbiamo seguito Chiara De
                  Bortoli durante la settimana del matchday del 10 novembre 2021 —
                  allenamenti, ricezione, conversazioni a bordo campo, atmosfera del
                  PalaFenera — costruendo un ritratto che è stato anche un manifesto
                  sul significato di responsabilità e supporto silenzioso. Poi una
                  troupe ha documentato il match dall'angolo del campo che il pubblico
                  non vede mai.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <div className="mx-auto max-w-[1100px]" data-testid="video-essere-libero">
              <WistiaPlayer mediaId="91kjfpqq3t" aspect={1.7777777777777777} />
              <p
                className="mt-4 text-[14px] leading-[1.55] text-mandrogni"
                style={{ fontStyle: "italic" }}
              >
                Essere Libero · Chiara De Bortoli racconta il ruolo del libero.
                PalaFenera, 10 novembre 2021.
              </p>
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.18}>
            <figure className="mx-auto max-w-[760px] mt-16 md:mt-24 text-center">
              <blockquote
                className="font-display text-dodgers leading-[1.05]"
                style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.5rem)", letterSpacing: "-0.015em" }}
              >
                <span className="text-lime" aria-hidden="true">"</span>
                Per me essere libero vuol dire mantenere la calma nei momenti di
                difficoltà. Le compagne devono sapere che ci sei, anche quando non ti
                vedono.
                <span className="text-lime" aria-hidden="true">"</span>
              </blockquote>
              <figcaption className="mt-6 font-condensed uppercase tracking-widest text-[13px] opacity-75">
                — Chiara De Bortoli, Libero del Chieri '76
              </figcaption>
            </figure>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Un format. Tre canali. Una storia.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  La forza del modello platform-content sta nell'integrazione tra
                  produzione editoriale e architettura di distribuzione. Abbiamo
                  gestito il progetto end-to-end, dall'idea iniziale all'amplificazione
                  cross-canale, con un'unica regia che ha tenuto insieme messaggio,
                  ritmo e audience.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Idea e concept"
                body="Definizione dell'angolo narrativo del format 'Essere Libero' applicato a Chiara De Bortoli: il libero come figura di calma, di responsabilità invisibile, di supporto alle compagne. Scelta del taglio editoriale — più ritratto che reportage — per uscire dai codici del recap sportivo classico."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Storyboard e produzione"
                body="Storyboard completo del video, definizione delle inquadrature in palazzetto, della scaletta delle interviste, dei momenti del matchday da catturare a livello fotografico. Produzione integrale: troupe video, fotografo, regia, post-produzione, articolo editoriale lungo per i canali Problemi di Volley."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Seeding su tre canali"
                body="Distribuzione coordinata su tre canali con voci differenziate: Problemi di Volley (community proprietaria OnSports, 87K+ follower) come centro editoriale del format; Reale Mutua Fenera Chieri '76 (canali ufficiali del club) per il legame con la fanbase volley; Reale Mutua Assicurazioni (canali corporate brand) per allargare il messaggio al pubblico non-volley."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Amplificazione"
                body="Calendario di pubblicazione costruito attorno alla settimana del matchday per massimizzare la curva di attenzione. Format derivati (clip social, caroselli, post statici) tagliati ad hoc per ogni canale e per le loro audience specifiche."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il backstage e il match.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione del photo report: il backstage degli allenamenti, le
                  interviste, l'atmosfera del PalaFenera e i momenti chiave del match
                  con i Fedelissimi.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-4 md:grid-cols-12 gap-3"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              // Layout magazine: slot 1 e 5 più larghi (6 col su desktop), gli altri 4 col.
              const wide = i === 0 || i === 4;
              const wrapperClass = wide
                ? "col-span-4 md:col-span-6"
                : "col-span-4 md:col-span-4";
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 6) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "1 / 1" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 6) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-auto object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 7 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Tre canali. Un'unica narrazione amplificata.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="590.000+" label="Impressions cross-canale" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="19.000+" label="Interazioni generate" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="3" label="Canali di amplificazione integrati" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="1" label="Format editoriale proprietario" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati aggregati di campagna su Problemi di Volley, canali ufficiali Reale
            Mutua Fenera Chieri '76 e Reale Mutua Assicurazioni nelle quattro
            settimane successive alla pubblicazione del format (novembre 2021).
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Voce autoriale
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il format non è stato percepito come contenuto sponsorizzato. È
                  stato letto come pezzo editoriale verticale sul volley, con Reale
                  Mutua come supporter visibile ma non protagonista. La differenza si
                  è vista nella qualità delle interazioni — commenti, salvataggi,
                  condivisioni — molto sopra la media dei contenuti brand puri sui
                  canali coinvolti.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Cross-pollination tra audience
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  I tre canali avevano pubblici parzialmente sovrapposti e
                  parzialmente diversi: la community volley (Problemi di Volley), la
                  fanbase del club (Chieri '76), il pubblico corporate del brand
                  (Reale Mutua). Distribuendo il format con voce coordinata ma
                  calibrata per ogni audience, abbiamo creato un'osmosi tra le tre —
                  facendo crescere ognuna grazie all'esposizione delle altre.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Asset duraturo
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il video, il photo report e l'articolo non sono morti con la
                  settimana di lancio. Sono diventati parte del catalogo editoriale
                  di Problemi di Volley e del club, continuando a generare visite e
                  interazioni nei mesi successivi. È la differenza tra un contenuto
                  sponsorizzato e un format proprietario: il primo si esaurisce, il
                  secondo continua a lavorare.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Clienti">
                  Reale Mutua Assicurazioni · Reale Mutua Fenera Chieri '76
                </SchedaRow>
                <SchedaRow label="Progetto">
                  Essere Libero · Chiara De Bortoli
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Idea &amp; concept · Storyboard<br />
                  Produzione video e foto · Articolo editoriale<br />
                  Seeding e amplificazione cross-canale
                </SchedaRow>
                <SchedaRow label="Format">
                  IP proprietaria Problemi di Volley<br />
                  (community editoriale OnSports)
                </SchedaRow>
                <SchedaRow label="Anno">Novembre 2021</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai una sponsorship che vorresti entrasse in un format invece di
              commissionarne uno? Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con brand e property che vogliono uscire dalla logica del
              contenuto sponsorizzato per entrare dentro narrazioni editoriali
              credibili. La community Problemi di Volley è uno dei luoghi dove queste
              storie possono prendere vita.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReaeleMutuaEssereLibero;
